import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Scissors, MapPin, Phone, MessageCircle, Clock, Instagram, Facebook, Youtube } from 'lucide-react';
import { colors, radius } from '../designSystem';

interface GalleryItem {
  url: string;
  type: 'image' | 'video';
}

interface HeroSectionProps {
  businessName: string;
  address?: string;
  logoSrc?: string;
  galleryItems: GalleryItem[];
  onGalleryClick?: () => void;
  onCall: () => void;
  onWhatsApp?: () => void;
  onNavigate: () => void;
  onShowHours: () => void;
  onInstagram?: () => void;
  onFacebook?: () => void;
  onTikTok?: () => void;
  onYouTube?: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  businessName,
  address,
  logoSrc,
  galleryItems,
  onGalleryClick,
  onCall,
  onWhatsApp,
  onNavigate,
  onShowHours,
  onInstagram,
  onFacebook,
  onTikTok,
  onYouTube,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? galleryItems.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === galleryItems.length - 1 ? 0 : prev + 1
    );
  };

  const hasGallery = galleryItems.length > 0;
  const currentItem = hasGallery ? galleryItems[currentImageIndex] : null;

  return (
    <div className="bg-white">
      {/* Gallery Section with White Gradient Blur at Bottom */}
      <div className="relative">
        {hasGallery ? (
          <div
            className="relative w-full overflow-hidden cursor-pointer"
            style={{ height: '280px' }}
            onClick={onGalleryClick}
          >
            {currentItem?.type === 'video' ? (
              <video
                src={currentItem.url}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <img
                src={currentItem?.url}
                alt=""
                className="w-full h-full object-cover"
              />
            )}

            {galleryItems.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 text-black z-10"
                  style={{ borderRadius: '50%' }}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                <button
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 text-black z-10"
                  style={{ borderRadius: '50%' }}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>

                <div
                  className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 text-white text-sm z-10"
                  style={{ borderRadius: radius.full }}
                >
                  {currentImageIndex + 1} / {galleryItems.length}
                </div>
              </>
            )}
          </div>
        ) : (
          <div
            className="w-full"
            style={{
              height: '280px',
              backgroundColor: colors.secondary,
            }}
          />
        )}

        {/* Subtle White Gradient Transition */}
        <div
          className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{
            height: '100px',
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.3) 25%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.95) 75%, rgba(255, 255, 255, 1) 100%)',
          }}
        />
      </div>

      {/* Overlapping Logo - Clean without gradient */}
      <div className="relative flex flex-col items-center" style={{ marginTop: '-70px' }}>
        <div className="relative">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={businessName}
              className="w-32 h-32 object-cover"
              style={{
                borderRadius: '50%',
                border: '4px solid white',
              }}
            />
          ) : (
            <div
              className="w-32 h-32 flex items-center justify-center"
              style={{
                borderRadius: '50%',
                backgroundColor: colors.secondary,
                border: '4px solid white',
              }}
            >
              <Scissors className="h-12 w-12" style={{ color: colors.accent }} />
            </div>
          )}
        </div>

        {/* Business Name */}
        <h1
          className="text-2xl font-bold mt-3 text-center px-6"
          style={{ color: colors.textPrimary }}
        >
          {businessName}
        </h1>

        {/* Address */}
        {address && (
          <div className="flex items-center gap-2 mt-2 text-sm px-6" style={{ color: colors.textSecondary }}>
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="text-center">{address}</span>
          </div>
        )}

        {/* Quick Actions - Compact */}
        <div className="flex items-center justify-center gap-3 mt-4 px-6">
          <button
            onClick={onCall}
            className="p-2"
            style={{
              borderRadius: '50%',
              backgroundColor: colors.secondary,
            }}
          >
            <Phone className="h-4 w-4" style={{ color: colors.accent }} />
          </button>

          {onWhatsApp && (
            <button
              onClick={onWhatsApp}
              className="p-2"
              style={{
                borderRadius: '50%',
                backgroundColor: colors.secondary,
              }}
            >
              <MessageCircle className="h-4 w-4" style={{ color: colors.accent }} />
            </button>
          )}

          <button
            onClick={onNavigate}
            className="p-2"
            style={{
              borderRadius: '50%',
              backgroundColor: colors.secondary,
            }}
          >
            <MapPin className="h-4 w-4" style={{ color: colors.accent }} />
          </button>

          <button
            onClick={onShowHours}
            className="p-2"
            style={{
              borderRadius: '50%',
              backgroundColor: colors.secondary,
            }}
          >
            <Clock className="h-4 w-4" style={{ color: colors.accent }} />
          </button>

          {onInstagram && (
            <button
              onClick={onInstagram}
              className="p-2"
              style={{
                borderRadius: '50%',
                backgroundColor: colors.secondary,
              }}
            >
              <Instagram className="h-4 w-4" style={{ color: '#EC4899' }} />
            </button>
          )}

          {onFacebook && (
            <button
              onClick={onFacebook}
              className="p-2"
              style={{
                borderRadius: '50%',
                backgroundColor: colors.secondary,
              }}
            >
              <Facebook className="h-4 w-4" style={{ color: colors.accent }} />
            </button>
          )}

          {onTikTok && (
            <button
              onClick={onTikTok}
              className="p-2"
              style={{
                borderRadius: '50%',
                backgroundColor: colors.secondary,
              }}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#000000' }}>
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </button>
          )}

          {onYouTube && (
            <button
              onClick={onYouTube}
              className="p-2"
              style={{
                borderRadius: '50%',
                backgroundColor: colors.secondary,
              }}
            >
              <Youtube className="h-4 w-4" style={{ color: '#FF0000' }} />
            </button>
          )}
        </div>
      </div>

      {/* Bottom Border */}
      <div className="pb-6 border-b mt-4" style={{ borderColor: colors.divider }} />
    </div>
  );
};
